import { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { canViewActual, useAuth } from "../lib/auth";
import { PageHeader, Section } from "../components/Primitives";
import ManagementDashboard from "./ManagementDashboard";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    BarChart,
    Bar,
} from "recharts";
import { MarginCell } from "../components/MarginCell";
import { fmtMoney } from "../lib/money";
import { COUNTRIES } from "../lib/regions";
import { ArrowUp, ArrowDown, ArrowUpDown } from "lucide-react";

const fmtNum = (v) => (v || 0).toLocaleString("en-US");

const PRESETS = [
    { value: 7, label: "Last 7 days" },
    { value: 30, label: "Last 30 days" },
    { value: 60, label: "Last 60 days" },
    { value: 90, label: "Last 90 days" },
];
const _todayISO = () => new Date().toISOString().slice(0, 10);
const _daysAgoISO = (n) => {
    const d = new Date();
    d.setDate(d.getDate() - (n - 1));
    return d.toISOString().slice(0, 10);
};

// Map a stored region value ("Malaysia", "MY", "APAC") → its short code for the market filter.
const _codeByName = new Map(COUNTRIES.map((c) => [c.name.toLowerCase(), c.code]));
const _validCodes = new Set(COUNTRIES.map((c) => c.code));
const regionCode = (v) => {
    if (!v) return "";
    const s = String(v).trim();
    if (!s) return "";
    const up = s.toUpperCase();
    if (_validCodes.has(up)) return up;
    const byName = _codeByName.get(s.toLowerCase());
    if (byName) return byName;
    return up.slice(0, 4);
};

export default function Dashboard() {
    const { user } = useAuth();
    // Management role gets its own region-grouped variant.
    if (user?.role === "management") return <ManagementDashboard />;
    return <MainDashboard user={user} />;
}

function MainDashboard({ user }) {
    const [daily, setDaily] = useState([]);
    const [summary, setSummary] = useState([]);
    const [campaigns, setCampaigns] = useState([]);
    const [loading, setLoading] = useState(true);
    // Date range: a preset (7/30/60/90) or "custom" with explicit from/to dates. Keeping the
    // window tight is what keeps the DV360 branch of /api/campaigns fast (backend caches the
    // aggregation per campaigns × dates), so repeat loads over the same window stay quick.
    const [preset, setPreset] = useState(30);
    const [dateFrom, setDateFrom] = useState(_daysAgoISO(30));
    const [dateTo, setDateTo] = useState(_todayISO());
    const [market, setMarket] = useState("all");
    const [sortKey, setSortKey] = useState("display_spend");
    const [sortDir, setSortDir] = useState("desc");

    useEffect(() => {
        let ignore = false;
        let timer;
        // DV360 metrics come from a cold-start TTL cache that returns empty on the first
        // request and warms in a background thread. The Meta/TikTok tiles render immediately;
        // when the campaigns list shows DV360 exists but the summary has no DV360 rows yet,
        // we re-fetch a few times so the warmed DV360 numbers appear without a manual reload.
        const run = async (attempt) => {
            if (attempt === 0) setLoading(true);
            try {
                const results = await Promise.allSettled([
                    api.get(`/reports/daily?date_from=${dateFrom}&date_to=${dateTo}`),
                    api.get(`/reports/summary?date_from=${dateFrom}&date_to=${dateTo}`),
                    api.get(`/campaigns?has_metrics=false&date_from=${dateFrom}&date_to=${dateTo}`),
                ]);
                if (ignore) return;
                const dailyData = results[0].status === "fulfilled" ? results[0].value.data : [];
                const summaryData = results[1].status === "fulfilled" ? results[1].value.data : [];
                const campData = results[2].status === "fulfilled" ? results[2].value.data || [] : [];
                setDaily(dailyData);
                setSummary(summaryData);
                setCampaigns(campData);
                const dvExpected = campData.some((c) => (c.platform || "").toLowerCase() === "dv360");
                const dvPresent = summaryData.some((r) => (r.platform || "").toLowerCase() === "dv360");
                if (dvExpected && !dvPresent && attempt < 4) {
                    timer = setTimeout(() => run(attempt + 1), 6000);
                }
            } finally {
                if (!ignore && attempt === 0) setLoading(false);
            }
        };
        run(0);
        return () => {
            ignore = true;
            if (timer) clearTimeout(timer);
        };
    }, [dateFrom, dateTo]);

    const showInternal = canViewActual(user);

    // Market (region) options come from every fetched row that carries a region tag.
    const marketOptions = useMemo(() => {
        const map = {};
        [...summary, ...daily, ...campaigns].forEach((r) => {
            const code = regionCode(r.region);
            if (code && !map[code]) map[code] = r.region;
        });
        return Object.entries(map)
            .map(([code, label]) => ({ code, label }))
            .sort((a, b) => a.label.localeCompare(b.label));
    }, [summary, daily, campaigns]);

    // Client-side market filter applied to every dataset before the memos crunch numbers.
    const fSummary = useMemo(
        () => (market === "all" ? summary : summary.filter((r) => regionCode(r.region) === market)),
        [summary, market]
    );
    const fDaily = useMemo(
        () => (market === "all" ? daily : daily.filter((r) => regionCode(r.region) === market)),
        [daily, market]
    );
    const fCampaigns = useMemo(
        () => (market === "all" ? campaigns : campaigns.filter((r) => regionCode(r.region) === market)),
        [campaigns, market]
    );

    // Per-platform impressions/clicks breakdown (Meta vs DV360 vs Google etc.)
    const countersByPlatform = useMemo(() => {
        const t = {};
        fSummary.forEach((r) => {
            const p = (r.platform || "other").toLowerCase();
            if (!t[p]) t[p] = { platform: p, impressions: 0, clicks: 0 };
            t[p].impressions += r.impressions || 0;
            t[p].clicks += r.clicks || 0;
        });
        return Object.values(t)
            .map((b) => ({ ...b, ctr: b.impressions > 0 ? (b.clicks / b.impressions) * 100 : 0 }))
            .sort((a, b) => b.impressions - a.impressions);
    }, [fSummary]);

    // Per-platform: counts (active / ongoing / delivered) + spend + avg CPM/CTR (currency-aware)
    const platformTotals = useMemo(() => {
        const today = new Date().toISOString().slice(0, 10);
        const t = {};
        fCampaigns.forEach((c) => {
            const p = (c.platform || "other").toLowerCase();
            if (!t[p]) t[p] = { platform: p, active_count: 0, ongoing_count: 0, delivered_count: 0, spend_by_currency: {}, imp_by_currency: {}, clicks_by_currency: {}, total_imp: 0, total_clicks: 0 };
            if (c.status === "active") t[p].active_count += 1;
            const isOngoing = c.status === "active" && (!c.end_date || c.end_date >= today);
            if (isOngoing) t[p].ongoing_count += 1;
            if (c.end_date && c.end_date < today) t[p].delivered_count += 1;
        });
        fSummary.forEach((r) => {
            const p = (r.platform || "other").toLowerCase();
            if (!t[p]) t[p] = { platform: p, active_count: 0, ongoing_count: 0, delivered_count: 0, spend_by_currency: {}, imp_by_currency: {}, clicks_by_currency: {}, total_imp: 0, total_clicks: 0 };
            const cur = (r.currency || "USD").toUpperCase();
            const spend = showInternal ? (r.actual_spend || 0) : (r.display_spend || 0);
            t[p].spend_by_currency[cur] = (t[p].spend_by_currency[cur] || 0) + spend;
            t[p].imp_by_currency[cur] = (t[p].imp_by_currency[cur] || 0) + (r.impressions || 0);
            t[p].clicks_by_currency[cur] = (t[p].clicks_by_currency[cur] || 0) + (r.clicks || 0);
            t[p].total_imp += r.impressions || 0;
            t[p].total_clicks += r.clicks || 0;
        });
        return Object.values(t)
            .map((b) => {
                const cur_stats = Object.keys(b.spend_by_currency).map((cur) => {
                    const spend = b.spend_by_currency[cur] || 0;
                    const imp = b.imp_by_currency[cur] || 0;
                    const clicks = b.clicks_by_currency[cur] || 0;
                    return {
                        currency: cur,
                        cpm: imp > 0 ? (spend / imp) * 1000 : 0,
                        ctr: imp > 0 ? (clicks / imp) * 100 : 0,
                        spend,
                    };
                }).sort((a, b2) => b2.spend - a.spend);
                return { ...b, cur_stats };
            })
            .filter((b) => b.active_count > 0 || b.delivered_count > 0 || b.cur_stats.length > 0)
            .sort((a, b) => b.active_count - a.active_count);
    }, [fCampaigns, fSummary, showInternal]);

    // Per-currency money buckets
    const byCurrency = useMemo(() => {
        const map = {};
        fSummary.forEach((r) => {
            const cur = (r.currency || "USD").toUpperCase();
            const b = map[cur] || (map[cur] = { currency: cur, display: 0, actual: 0, budget: 0, campaigns: 0 });
            b.display += r.display_spend || 0;
            b.actual += r.actual_spend || 0;
            b.budget += r.client_budget || 0;
            b.campaigns += 1;
        });
        return Object.values(map)
            .map((b) => ({
                ...b,
                margin: b.display - b.actual,
                margin_pct: b.display > 0 ? ((b.display - b.actual) / b.display) * 100 : 0,
            }))
            .sort((a, b) => b.display - a.display);
    }, [fSummary]);

    const trend = useMemo(() => {
        const byDate = {};
        fDaily.forEach((r) => {
            const k = r.date;
            if (!byDate[k]) byDate[k] = { date: k, display: 0, actual: 0, impressions: 0 };
            byDate[k].display += r.display_spend;
            byDate[k].actual += r.actual_spend || 0;
            byDate[k].impressions += r.impressions;
        });
        return Object.values(byDate).sort((a, b) => a.date.localeCompare(b.date));
    }, [fDaily]);

    const byPlatform = useMemo(() => {
        const t = {};
        fSummary.forEach((r) => {
            if (!t[r.platform]) t[r.platform] = { platform: r.platform, display: 0, actual: 0 };
            t[r.platform].display += r.display_spend;
            t[r.platform].actual += r.actual_spend || 0;
        });
        return Object.values(t);
    }, [fSummary]);

    // Top campaigns — sortable by the chosen column/direction.
    const topCampaigns = useMemo(() => {
        const dir = sortDir === "asc" ? 1 : -1;
        const val = (c) => {
            if (sortKey === "campaign_name") return (c.campaign_name || "").toLowerCase();
            if (sortKey === "margin") return c.margin || 0;
            return c.display_spend || 0;
        };
        return [...fSummary]
            .sort((a, b) => {
                const va = val(a), vb = val(b);
                if (va < vb) return -1 * dir;
                if (va > vb) return 1 * dir;
                return 0;
            })
            .slice(0, 5);
    }, [fSummary, sortKey, sortDir]);

    const toggleSort = (key) => {
        if (sortKey === key) {
            setSortDir((d) => (d === "asc" ? "desc" : "asc"));
        } else {
            setSortKey(key);
            setSortDir(key === "campaign_name" ? "asc" : "desc");
        }
    };

    const SortIcon = ({ k }) => {
        if (sortKey !== k) return <ArrowUpDown className="h-3 w-3 text-zinc-400" />;
        return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
    };

    const trendCurrency = byCurrency[0]?.currency || "USD";

    return (
        <div data-testid="dashboard-page">
            <PageHeader
                eyebrow={user?.role.replace("_", " ")}
                title={`Hello, ${user?.full_name?.split(" ")[0] || "there"}`}
                subtitle={
                    showInternal
                        ? "Margin-protected view. Numbers in amber are internal-only and never visible to advertisers."
                        : "Your committed commercial view across all campaigns."
                }
                actions={
                    <div className="flex flex-wrap items-end gap-3" data-testid="dashboard-filters">
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Range</label>
                            <select
                                value={preset}
                                onChange={(e) => {
                                    const v = e.target.value === "custom" ? "custom" : parseInt(e.target.value, 10);
                                    setPreset(v);
                                    if (v !== "custom") {
                                        setDateFrom(_daysAgoISO(v));
                                        setDateTo(_todayISO());
                                    }
                                }}
                                className="px-2 py-1.5 bg-white border border-zinc-300 rounded text-sm"
                                data-testid="dashboard-range-select"
                            >
                                {PRESETS.map((p) => (
                                    <option key={p.value} value={p.value}>{p.label}</option>
                                ))}
                                <option value="custom">Custom range</option>
                            </select>
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500">From</label>
                            <input
                                type="date"
                                value={dateFrom}
                                max={dateTo}
                                onChange={(e) => { setDateFrom(e.target.value); setPreset("custom"); }}
                                className="px-2 py-1.5 bg-white border border-zinc-300 rounded text-sm"
                                data-testid="dashboard-date-from"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500">To</label>
                            <input
                                type="date"
                                value={dateTo}
                                min={dateFrom}
                                max={_todayISO()}
                                onChange={(e) => { setDateTo(e.target.value); setPreset("custom"); }}
                                className="px-2 py-1.5 bg-white border border-zinc-300 rounded text-sm"
                                data-testid="dashboard-date-to"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <label className="text-[11px] uppercase tracking-wider text-zinc-500">Market</label>
                            <select
                                value={market}
                                onChange={(e) => setMarket(e.target.value)}
                                className="px-2 py-1.5 bg-white border border-zinc-300 rounded text-sm"
                                data-testid="dashboard-market-select"
                            >
                                <option value="all">All markets</option>
                                {marketOptions.map((m) => (
                                    <option key={m.code} value={m.code}>{m.label} ({m.code})</option>
                                ))}
                            </select>
                        </div>
                    </div>
                }
            />

            {/* Top tiles — per-platform Active Campaigns + Total Spend */}
            <div className="grid gap-4 mb-6" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(380px, 1fr))` }} data-testid="dashboard-top-platforms">
                {platformTotals.length === 0 && (
                    <div className="bg-white border border-zinc-200 p-8 text-center text-zinc-500 text-sm">
                        No platform data yet. Connect Meta or DV360 to populate.
                    </div>
                )}
                {platformTotals.map((b) => {
                    const label = b.platform === "meta" ? "Meta" : b.platform === "dv360" ? "DV360" : b.platform === "google" ? "Google" : b.platform;
                    return (
                        <div key={b.platform} className="bg-white border border-zinc-200 rounded p-5" data-testid={`platform-top-card-${b.platform}`}>
                            <div className="flex items-center justify-between mb-4">
                                <div className="overline">{label}</div>
                            </div>

                            {/* Campaign counts */}
                            <div className="grid grid-cols-3 gap-x-3 gap-y-4 mb-4">
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">Active</div>
                                    <div className="font-display text-xl tabular-nums text-zinc-900 mt-0.5" data-testid={`active-campaigns-${b.platform}`}>
                                        {fmtNum(b.active_count)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">Ongoing</div>
                                    <div className="font-display text-xl tabular-nums wl-accent-text mt-0.5" data-testid={`ongoing-campaigns-${b.platform}`}>
                                        {fmtNum(b.ongoing_count)}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-zinc-400 whitespace-nowrap">Delivered</div>
                                    <div className="font-display text-xl tabular-nums text-zinc-700 mt-0.5" data-testid={`delivered-campaigns-${b.platform}`}>
                                        {fmtNum(b.delivered_count)}
                                    </div>
                                </div>
                            </div>

                            {/* Per-currency CPM + CTR */}
                            <div className="pt-3 border-t border-zinc-200">
                                {b.cur_stats.length === 0 ? (
                                    <div className="text-xs text-zinc-400">No spend yet</div>
                                ) : (
                                    <div className="space-y-2.5">
                                        {b.cur_stats.map((s) => (
                                            <div key={s.currency} className="flex items-center justify-between gap-3" data-testid={`platform-cur-stats-${b.platform}-${s.currency}`}>
                                                <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-100 text-zinc-700 whitespace-nowrap">{s.currency}</span>
                                                <div className="flex items-baseline gap-5 flex-1 justify-end">
                                                    <div className="text-right">
                                                        <div className="text-[10px] uppercase tracking-wider text-zinc-400">Avg CPM</div>
                                                        <div className="font-display text-base tabular-nums text-zinc-900 whitespace-nowrap" data-testid={`avg-cpm-${b.platform}-${s.currency}`}>
                                                            {fmtMoney(s.cpm, s.currency)}
                                                        </div>
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="text-[10px] uppercase tracking-wider text-zinc-400">Avg CTR</div>
                                                        <div className="font-display text-base tabular-nums wl-accent-text" data-testid={`avg-ctr-${b.platform}-${s.currency}`}>
                                                            {s.ctr.toFixed(2)}%
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Per-platform impressions / clicks */}
            <Section title="Impressions & clicks by platform" testId="section-by-platform">
                {countersByPlatform.length === 0 ? (
                    <div className="bg-white border border-zinc-200 p-8 text-center text-zinc-500 text-sm">
                        No platform data yet. Sync a Meta or DV360 connection to populate.
                    </div>
                ) : (
                    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(260px, 1fr))` }}>
                        {countersByPlatform.map((b) => {
                            const platformLabel = b.platform === "meta" ? "Meta" : b.platform === "dv360" ? "DV360" : b.platform === "google" ? "Google" : b.platform;
                            return (
                                <div key={b.platform} className="bg-white border border-zinc-200 rounded p-5" data-testid={`platform-card-${b.platform}`}>
                                    <div className="flex items-center justify-between mb-3">
                                        <div className="overline">{platformLabel}</div>
                                        <span className="text-[10px] uppercase tracking-wider text-zinc-400">{b.ctr.toFixed(2)}% CTR</span>
                                    </div>
                                    <div className="grid grid-cols-2 gap-4">
                                        <div>
                                            <div className="overline text-zinc-400">Impressions</div>
                                            <div className="font-display text-2xl tabular-nums text-zinc-900 mt-0.5">{fmtNum(b.impressions)}</div>
                                        </div>
                                        <div>
                                            <div className="overline text-zinc-400">Clicks</div>
                                            <div className="font-display text-2xl tabular-nums wl-accent-text mt-0.5">{fmtNum(b.clicks)}</div>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Section>

            {/* Per-currency money breakdown */}
            <Section title="Budget & Margin Overview" testId="section-by-currency">
                {byCurrency.length === 0 ? (
                    <div className="bg-white border border-zinc-200 p-8 text-center text-zinc-500 text-sm">
                        No spend data yet. Sync a connection to see currency-aware totals here.
                    </div>
                ) : (
                    <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(auto-fit, minmax(300px, 1fr))` }}>
                        {byCurrency.map((b) => {
                            const budgetPct = b.budget > 0 ? Math.min(100, (b.display / b.budget) * 100) : 0;
                            return (
                                <div key={b.currency} className="bg-white border border-zinc-200 p-5" data-testid={`currency-card-${b.currency}`}>
                                    {/* Header */}
                                    <div className="flex items-center justify-between mb-4">
                                        <div className="overline">Currency</div>
                                        <span className="px-2 py-0.5 rounded text-[10px] uppercase tracking-wider bg-zinc-200 text-zinc-700">{b.currency}</span>
                                    </div>
                                    {/* Campaign Budget headline */}
                                    <div>
                                        <div className="text-[11px] uppercase tracking-wider text-zinc-500">Campaign Budget</div>
                                        <div className="font-display text-2xl font-bold tabular-nums wl-accent-text mt-0.5 whitespace-nowrap">
                                            {fmtMoney(b.display, b.currency)}
                                        </div>
                                    </div>
                                    {/* Budget progress */}
                                    <div className="mt-3">
                                        <div className="flex items-baseline justify-between text-[11px] text-zinc-500 mb-1">
                                            <span>of Budget</span>
                                            <span className="tabular-nums text-zinc-700 font-medium whitespace-nowrap">{fmtMoney(b.budget, b.currency)}</span>
                                        </div>
                                        <div className="w-full h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                                            <div className="h-full" style={{ width: `${budgetPct}%`, background: budgetPct >= 100 ? "#ef4444" : "var(--wl-primary-hex)" }} />
                                        </div>
                                    </div>
                                    {/* Footer metrics — stacked rows, clear spacing, no horizontal cramming */}
                                    <div className="mt-4 pt-3 border-t border-zinc-200 space-y-2">
                                        {showInternal ? (
                                            <>
                                                <div className="flex items-baseline justify-between gap-3">
                                                    <span className="text-[10px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">Campaign Spends</span>
                                                    <span className="font-display text-sm tabular-nums text-zinc-700 whitespace-nowrap">{fmtMoney(b.actual, b.currency)}</span>
                                                </div>
                                                <div className="flex items-baseline justify-between gap-3">
                                                    <span className="text-[10px] uppercase tracking-wider text-amber-500 whitespace-nowrap">Margin</span>
                                                    <span className={`text-right whitespace-nowrap`}>
                                                        <span className={`font-display text-sm tabular-nums font-medium ${b.margin >= 0 ? "text-amber-500" : "text-red-500"}`}>
                                                            {fmtMoney(b.margin, b.currency)}
                                                        </span>
                                                        <span className={`block text-[10px] tabular-nums ${b.margin >= 0 ? "text-amber-500/70" : "text-red-500/70"}`}>
                                                            {b.margin_pct.toFixed(1)}%
                                                        </span>
                                                    </span>
                                                </div>
                                            </>
                                        ) : (
                                            <div className="flex items-baseline justify-between gap-3">
                                                <span className="text-[10px] uppercase tracking-wider text-zinc-500 whitespace-nowrap">Campaigns</span>
                                                <span className="font-display text-sm tabular-nums text-zinc-700">{b.campaigns}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </Section>

            <Section title={`Spend trend — ${trendCurrency}`} testId="section-trend">
                <div className="bg-white border border-zinc-200 p-5">
                    {byCurrency.length > 1 && (
                        <div className="text-[11px] text-amber-500 bg-amber-500/5 border border-amber-500/20 px-2 py-1 rounded mb-3 inline-block">
                            ⚠ Chart aggregates raw amounts across {byCurrency.length} currencies ({byCurrency.map((c) => c.currency).join(", ")}). Filter by ad-account or advertiser on the report pages for accurate per-currency totals.
                        </div>
                    )}
                    <div style={{ width: "100%", height: 260 }}>
                        <ResponsiveContainer>
                            <LineChart data={trend} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
                                <CartesianGrid stroke="#e4e4e7" vertical={false} />
                                <XAxis dataKey="date" tick={{ fill: "#52525b", fontSize: 11 }} tickLine={false} axisLine={false} />
                                <YAxis tick={{ fill: "#52525b", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e4e4e7", borderRadius: 6, color: "#0a0a0a" }} formatter={(v) => fmtMoney(v, trendCurrency)} />
                                <Line type="monotone" dataKey="display" stroke="var(--wl-primary-hex)" strokeWidth={2} dot={false} name="Campaign Budget" />
                                {showInternal && (
                                    <Line type="monotone" dataKey="actual" stroke="#A1A1AA" strokeWidth={2} strokeDasharray="4 4" dot={false} name="Campaign Spends (internal)" />
                                )}
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            </Section>

            <div className="grid lg:grid-cols-2 gap-6 mb-10">
                <Section title="By platform" testId="section-platform">
                    <div className="bg-white border border-zinc-200 p-5">
                        <div style={{ width: "100%", height: 240 }}>
                            <ResponsiveContainer>
                                <BarChart data={byPlatform}>
                                    <CartesianGrid stroke="#e4e4e7" vertical={false} />
                                    <XAxis dataKey="platform" tick={{ fill: "#52525b", fontSize: 11 }} tickLine={false} axisLine={false} />
                                    <YAxis tick={{ fill: "#52525b", fontSize: 11 }} tickLine={false} axisLine={false} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                                    <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #e4e4e7", borderRadius: 6 }} formatter={(v) => fmtMoney(v, trendCurrency)} />
                                    <Bar dataKey="display" fill="var(--wl-primary-hex)" name="Campaign Budget" radius={[4, 4, 0, 0]} />
                                    {showInternal && <Bar dataKey="actual" fill="#a1a1aa" name="Campaign Spends" radius={[4, 4, 0, 0]} />}
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </Section>

                <Section title="Top 5 campaigns" testId="section-top-campaigns">
                    <div className="bg-white border border-zinc-200">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="bg-zinc-100 border-b border-zinc-200">
                                    <th className="p-3 text-left">
                                        <button onClick={() => toggleSort("campaign_name")} className="overline inline-flex items-center gap-1 hover:text-zinc-900" data-testid="sort-campaign">
                                            Campaign <SortIcon k="campaign_name" />
                                        </button>
                                    </th>
                                    <th className="p-3 text-right">
                                        <button onClick={() => toggleSort("display_spend")} className="overline inline-flex items-center gap-1 hover:text-zinc-900 ml-auto" data-testid="sort-budget">
                                            {user?.role === "advertiser" ? "Spend" : "Budget"} <SortIcon k="display_spend" />
                                        </button>
                                    </th>
                                    {showInternal && (
                                        <th className="p-3 text-right">
                                            <button onClick={() => toggleSort("margin")} className="overline inline-flex items-center gap-1 hover:text-zinc-900 ml-auto" data-testid="sort-margin">
                                                Margin <SortIcon k="margin" />
                                            </button>
                                        </th>
                                    )}
                                </tr>
                            </thead>
                            <tbody>
                                {topCampaigns.map((c) => (
                                    <tr key={c.campaign_id} className="border-b border-zinc-200 last:border-0">
                                        <td className="p-3">
                                            <div className="text-zinc-800 truncate max-w-[220px]" title={c.campaign_name}>{c.campaign_name}</div>
                                            <div className="text-xs text-zinc-500">{c.platform} · {c.advertiser_name || "—"}</div>
                                        </td>
                                        <td className="p-3 text-right tabular-nums text-zinc-800">{fmtMoney(c.display_spend, c.currency)}</td>
                                        {showInternal && (
                                            <td className="p-3 text-right">
                                                <MarginCell amount={c.margin} pct={c.margin_pct} currency={c.currency} />
                                            </td>
                                        )}
                                    </tr>
                                ))}
                                {!topCampaigns.length && !loading && (
                                    <tr>
                                        <td colSpan={showInternal ? 3 : 2} className="p-6 text-center text-zinc-500 text-sm">
                                            No campaign data yet.
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                </Section>
            </div>
        </div>
    );
}
